import React from 'react';
import { Check } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { apiClient } from '../api/client';
import type { PlanCode, CheckoutConfig } from '../types/apiResponses';

declare global {
  interface Window {
    Paddle?: {
      Checkout: {
        open: (options: {
          items: { priceId: string }[];
          customData?: Record<string, unknown>;
        }) => void;
      };
    };
  }
}

const PADDLE_SCRIPT_SRC = 'https://cdn.paddle.com/paddle/v2/paddle.js';

function loadPaddleScript(): Promise<void> {
  return new Promise((resolve, reject) => {
    if (window.Paddle) {
      resolve();
      return;
    }
    const existing = document.querySelector<HTMLScriptElement>(
      `script[src="${PADDLE_SCRIPT_SRC}"]`
    );
    if (existing) {
      existing.addEventListener('load', () => resolve());
      existing.addEventListener('error', () => reject(new Error('Failed to load Paddle.js')));
      return;
    }
    const script = document.createElement('script');
    script.src = PADDLE_SCRIPT_SRC;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Failed to load Paddle.js'));
    document.head.appendChild(script);
  });
}

async function openPaddleCheckout(cfg: CheckoutConfig): Promise<void> {
  if (!cfg.priceId) return;
  await loadPaddleScript();
  window.Paddle?.Checkout.open({
    items: [{ priceId: cfg.priceId }],
    customData: cfg.customData,
  });
}

const plans = [
  {
    name: "Starter",
    price: "€99",
    period: "/ay",
    description: "Küçük işletmeler için temel ESG ve karbon yönetimi",
    features: [
      "Karbon ayak izi hesaplama",
      "Temel ESG metrikleri",
      "Otomatik raporlama",
      "Email desteği"
    ]
  },
  {
    name: "Professional",
    price: "€299",
    period: "/ay",
    description: "Orta ölçekli işletmeler için kapsamlı çözüm",
    features: [
      "Gelişmiş ESG analizi",
      "Tedarik zinciri emisyonları",
      "AI destekli öngörüler",
      "7/24 öncelikli destek",
      "API erişimi"
    ],
    popular: true
  },
  {
    name: "Enterprise",
    price: "Özel",
    period: "",
    description: "Büyük kurumlar için özelleştirilmiş çözümler",
    features: [
      "Tam kapsamlı ESG yönetimi",
      "Özel entegrasyonlar",
      "Dedicated hesap yöneticisi",
      "Özelleştirilmiş raporlama",
      "Sınırsız kullanıcı"
    ]
  }
];

export default function Pricing() {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();

  const planCodes: Record<string, PlanCode> = {
    Starter: 'starter',
    Professional: 'professional',
    Enterprise: 'enterprise',
  };

  const handleCta = async (planName: string) => {
    const code = planCodes[planName];
    if (code === 'enterprise') {
      document.getElementById('contact')?.scrollIntoView?.({ behavior: 'smooth' });
      return;
    }
    if (!isAuthenticated) {
      navigate(`/signup?plan=${code}`);
      return;
    }
    try {
      const cfg = await apiClient.billing.checkout(code);
      if (cfg.configured && cfg.clientToken && cfg.priceId) {
        openPaddleCheckout(cfg);
      } else {
        navigate('/dashboard/settings?tab=billing');
      }
    } catch {
      navigate('/dashboard/settings?tab=billing');
    }
  };

  return (
    <section className="py-20 bg-white" id="pricing">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">
            Şeffaf Fiyatlandırma
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            İhtiyaçlarınıza uygun, ölçeklenebilir planlar
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {plans.map((plan, index) => (
            <div
              key={index}
              className={`bg-white p-8 rounded-xl ${
                plan.popular
                  ? 'ring-2 ring-green-800 shadow-xl'
                  : 'border border-gray-200'
              }`}
            >
              {plan.popular && (
                <span className="inline-block px-4 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800 mb-4">
                  En Popüler
                </span>
              )}
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                {plan.name}
              </h3>
              <div className="mb-4">
                <span className="text-4xl font-bold text-gray-900">{plan.price}</span>
                <span className="text-gray-600">{plan.period}</span>
              </div>
              <p className="text-gray-600 mb-6">{plan.description}</p>
              <button
                onClick={() => handleCta(plan.name)}
                className={`w-full mb-6 px-6 py-3 rounded-lg font-medium ${
                  plan.popular
                    ? 'bg-green-800 text-white hover:bg-green-700'
                    : 'border border-green-800 text-green-800 hover:bg-green-50'
                } transition-colors`}
              >
                {plan.name === "Enterprise" ? "İletişime Geçin" : "Hemen Başlayın"}
              </button>
              <ul className="space-y-4">
                {plan.features.map((feature, i) => (
                  <li key={i} className="flex items-start">
                    <Check className="h-5 w-5 text-green-800 mr-2 flex-shrink-0" />
                    <span className="text-gray-600">{feature}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}